import React, { useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import moment from 'moment'
import EstadoBadge from '../commons/EstadoBadge'
import capitalizeText from '../../utils/capitalizeText'
import ModalDetalleAdicion from './ModalDetalleAdicion'

/**
 * Renderiza icono de acción según estado y task
 */
const renderActionIcon = (eq, task, handlers, bloquearFinalizar = false, titleBloqueo = 'Sin cobertura de patrones — no se puede procesar') => {
  if (task === 'finalizar') {
    if (eq.estado === 'Cancelado') {
      // Cancelado - Sin acción
      return <i className="bi bi-x-square-fill text-muted" title="Cancelado" />
    } else if (eq.estado === 'Procesado') {
      // Procesado - Permite des-procesar (volver atrás)
      return (
        <i 
          className="bi bi-check-square-fill cursor-pointer text-warning" 
          onClick={() => handlers.onNoFinalize(eq)}
          title="Des-procesar (No Finalizar)"
        />
      )
    } else if (eq.estado === 'Pendiente' || eq.estado === 'Aplazado') {
      // Pendiente/Aplazado - Permite activar
      return (
        <i
          className="bi bi-pause-circle-fill cursor-pointer text-info"
          onClick={() => handlers.onActivate(eq)}
          title="Activar"
        />
      )
    } else if (eq.estado === 'En Proceso') {
      // En Proceso — equipo en calibración por un lote activo. Bloquear acción manual.
      // Spec lotes-estado-en-proceso-mediciones-condicionales (R8).
      return (
        <i
          className="bi bi-gear-wide-connected text-info"
          title="Equipo en calibración — administrar desde su lote"
          style={{ cursor: 'not-allowed', opacity: 0.7 }}
        />
      )
    } else {
      // Otros estados - bloquear si sin cobertura de patrones o sin visita vigente
      if (bloquearFinalizar) {
        return (
          <i
            className="bi bi-check-square text-muted"
            title={titleBloqueo}
            style={{ cursor: 'not-allowed', opacity: 0.4 }}
          />
        )
      }
      return (
        <i 
          className="bi bi-check-square cursor-pointer text-success" 
          onClick={() => handlers.onFinalize(eq)}
          title="Finalizar"
        />
      )
    }
  } else if (task === 'revisar') {
    if (eq.estado === 'Aprobado') {
      return (
        <i
          className="bi bi-patch-check-fill text-success cursor-pointer"
          onClick={() => handlers.onDesaprovar(eq)}
          title="Aprobado — click para desaprobar"
          style={{ cursor: 'pointer', fontSize: '1.2rem' }}
        />
      )
    }
    if (eq.estado === 'Finalizado') {
      // Solo Finalizado puede seleccionarse para aprobar
      return (
        <input
          type="checkbox"
          className="form-check-input cursor-pointer"
          onChange={() => handlers.onAprovarOneToggle(eq)}
          checked={handlers.equiSelectRef?.current?.some(item => item._id === eq._id)}
        />
      )
    }
    // Otros estados: no seleccionable
    return <i className="bi bi-dash text-muted" title={eq.estado} />
  }
  return null
}

/**
 * Opciones dropdown para task='finalizar'
 */
const renderFinalizarOptions = (eq, handlers, bloquearFinalizar = false, titleBloqueo = 'Sin cobertura de patrones — no se puede procesar') => {
  if (eq.estado === 'Cancelado') {
    return (
      <Dropdown.Item onClick={() => handlers.onActivate(eq)}>
        <i className="bi bi-arrow-counterclockwise text-success me-2"/>
        Restaurar equipo
      </Dropdown.Item>
    )
  } else if (eq.estado === 'Procesado') {
    return (
      <Dropdown.Item onClick={() => handlers.onNoFinalize(eq)}>
        <i className="bi bi-arrow-counterclockwise text-warning me-2"/>
        Des-procesar
      </Dropdown.Item>
    )
  } else if (eq.estado === 'Pendiente' || eq.estado === 'Aplazado') {
    return (
      <Dropdown.Item onClick={() => handlers.onActivate(eq)}>
        <i className="bi bi-play-circle text-info me-2"/>
        Activar
      </Dropdown.Item>
    )
  } else {
    return (
      <Dropdown.Item onClick={() => !bloquearFinalizar && handlers.onFinalize(eq)} disabled={bloquearFinalizar}>
        <i className={`bi bi-check-circle-fill me-2 ${bloquearFinalizar ? 'text-muted' : 'text-success'}`}/>
        {bloquearFinalizar ? `Finalizar (${titleBloqueo})` : 'Finalizar'}
      </Dropdown.Item>
    )
  }
}

/**
 * Opciones dropdown para task='revisar'
 */
const renderRevisarOptions = (eq, handlers) => {
  if (eq.estado === 'Aprobado') {
    return (
      <Dropdown.Item onClick={() => handlers.onDesaprovar(eq)}>
        <i className="bi bi-patch-check-fill me-2 text-success"/>
        Desaprobar
      </Dropdown.Item>
    )
  }
  if (eq.estado === 'Finalizado') {
    return (
      <Dropdown.Item onClick={() => handlers.onAprovarOneToggle(eq)}>
        <i className="bi bi-check-square me-2"/>
        {handlers.equiSelectRef?.current?.some(item => item._id === eq._id)
          ? 'Desmarcar'
          : 'Marcar para revisar'}
      </Dropdown.Item>
    )
  }
  return null
}

/**
 * Componente de fila de tabla con diseño compacto multi-línea
 * 
 * @param {Object} props
 * @param {Object} props.equipo - Objeto equipo de la OT
 * @param {number} props.index - Índice de la fila
 * @param {string} props.task - Tipo de tarea ('finalizar' o 'revisar')
 * @param {number} props.screenWidth - Ancho de pantalla para responsive
 * @param {Object} props.currentUser - Usuario actual
 * @param {Object} props.handlers - Handlers de acciones
 * @param {Function} props.handlers.onEdit - Editar equipo
 * @param {Function} props.handlers.onCancel - Cancelar equipo
 * @param {Function} props.handlers.onObserv - Ver observación
 * @param {Function} props.handlers.onFinalize - Finalizar equipo
 * @param {Function} props.handlers.onActivate - Activar equipo
 * @param {Function} props.handlers.onNoFinalize - No finalizar equipo
 * @param {Function} props.handlers.onAprovarOneToggle - Toggle checkbox revisión
 * @param {Function} props.handlers.onVerDetalle - Ver detalle móvil
 * @param {Object} props.handlers.equiSelectRef - Ref para selección múltiple
 */
const EquipoCompactRow = ({
  equipo,
  index,
  task,
  screenWidth,
  currentUser,
  handlers,
  patronIdsActivos = new Set(),
  hayVisitaVigente = true,
  // Info del grupo padre↔hijos calculada en TableEquiposCompact
  // (spec items-adicionales-equipo-dependiente / visualización grupos).
  // Formato: { grupoNum: 1, rol: 'padre'|'hijo', letra?: 'a'|'b'|... }
  grupoInfo = null,
}) => {
  const eq = equipo
  const [showAdicionModal, setShowAdicionModal] = useState(false);

  // Ingresado al laboratorio: tiene fechaIngreso y no tiene fechaSalida
  // (datos vienen de la colección equipmentInOt).
  const estaIngresado = Boolean(eq.fechaIngreso) && !eq.fechaSalida;

  // Calcular cobertura de patrones para este equipo
  const getPatronesCoverage = (patronesUtilizados, idsActivos) => {
    // Filtrar sub-arrays vacíos — la DB guarda [[]] cuando no hay patrones configurados
    const grupos = (patronesUtilizados || []).filter(subArr => Array.isArray(subArr) && subArr.length > 0);
    if (grupos.length === 0) return { tipo: 'sin-config', cubiertos: 0, total: 0 };
    const cubiertos = grupos.filter(subArr =>
      subArr.some(id => idsActivos.has(String(id)))
    ).length;
    const total = grupos.length;
    if (cubiertos === total) return { tipo: 'ok', cubiertos, total };
    if (cubiertos > 0) return { tipo: 'parcial', cubiertos, total };
    return { tipo: 'sin-cobertura', cubiertos: 0, total };
  };

  const patronesCoverage = getPatronesCoverage(
    eq.equipo?.item?.patronesUtilizados,
    patronIdsActivos
  );

  // Bloquear Finalizar si task=finalizar y sin cobertura de patrones O sin visita vigente
  const bloquearPorPatrones = task === 'finalizar' && patronesCoverage.tipo === 'sin-cobertura';
  const bloquearPorVisita   = task === 'finalizar' && !hayVisitaVigente;
  const bloquearFinalizar   = bloquearPorPatrones || bloquearPorVisita;
  const titleBloqueo = bloquearPorVisita
    ? 'Sin visita vigente — no se puede procesar'
    : 'Sin cobertura de patrones — no se puede procesar';
  
  // Validar que no sea responsable para deshabilitar acciones
  const esResponsable = currentUser && eq.responsable?._id === currentUser._id
  const puedeActuar = task === 'finalizar' ? esResponsable : true
  
  return (
    <>
    <tr 
      className={eq.estado === 'Cancelado' ? 'table-secondary' : ''}
      style={eq.adicion && eq.estado !== 'Cancelado' ? { backgroundColor: '#eef5ff' } : undefined}
    >
      {/* Columna índice */}
      <td className="text-center align-middle">{index + 1}</td>

      {/* Columna + Adición (tablet+)  e indicacion de relacion padres - dependiente */}
      {screenWidth >= 768 && (
        <td
          className="text-center align-middle"
          style={{ padding: '2px 1px', width: '36px', minWidth: '36px', maxWidth: '36px', overflow: 'hidden' }}
        >
          <div
            className="d-flex flex-column align-items-center"
            style={{ gap: '2px', lineHeight: 1, maxWidth: '100%' }}
          >
            {estaIngresado && (
              <i
                className="bi bi-geo-alt-fill text-primary"
                title={`Ingresado al laboratorio${eq.fechaIngreso ? ` el ${moment(eq.fechaIngreso).format('DD/MM/YYYY')}` : ''}`}
                style={{ fontSize: '0.8rem', lineHeight: 1 }}
              />
            )}

            {grupoInfo && (
              <span
                className={grupoInfo.rol === 'padre' ? 'bg-primary text-white' : 'bg-info text-dark'}
                style={{
                  display: 'inline-block',
                  fontSize: '0.55rem',
                  fontWeight: 700,
                  padding: '1px 3px',
                  lineHeight: 1.1,
                  whiteSpace: 'nowrap',
                  letterSpacing: '-0.03em',
                  borderRadius: '3px',
                  maxWidth: '100%',
                }}
                title={
                  grupoInfo.rol === 'padre'
                    ? `Padre del grupo ${grupoInfo.grupoNum}`
                    : `Dependiente ${grupoInfo.grupoNum}${grupoInfo.letra || ''} del grupo ${grupoInfo.grupoNum}`
                }
              >
                {grupoInfo.rol === 'padre'
                  ? `P${grupoInfo.grupoNum}`
                  : `D${grupoInfo.grupoNum}${grupoInfo.letra || ''}`}
              </span>
            )}

            {eq.adicion && (
              <i
                className="bi bi-check-circle-fill text-success"
                title="Ver detalle de adición"
                style={{ cursor: 'pointer', fontSize: '0.8rem', lineHeight: 1 }}
                onClick={() => setShowAdicionModal(true)}
              />
            )}
          </div>
        </td>
      )}
      
      {/* Columna Item */}
      <td className="align-middle">
        <a
          href={`/perfilEq?_id=${eq.equipo?._id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none fw-semibold"
        >
          {eq.snapshot?.item || 'Sin nombre'}
        </a>
      </td>
      
      {/* Columna Equipos (multi-línea) */}
      <td className="align-middle">
        <div className="small">
          <div>
            <strong>{eq.snapshot?.marca || 'N/A'}</strong>
            {eq.snapshot?.modelo && ` - ${eq.snapshot.modelo}`}
          </div>
          <div className="text-muted">
            S/N: {eq.snapshot?.serial || 'N/A'}
            {screenWidth >= 768 && eq.snapshot?.inventario && ` | Inv: ${eq.snapshot.inventario}`}
          </div>
        </div>
      </td>
      
      {/* Columna Ubicación (solo tablet+) */}
      {screenWidth >= 768 && (
        <td className="align-middle">
          <div className="small">
            <div className="fw-semibold">{capitalizeText(eq.snapshot?.sede || 'N/A')}</div>
            <div className="text-muted">{capitalizeText(eq.snapshot?.ubicacion || 'Sin ubicación')}</div>
          </div>
        </td>
      )}

      {/* Columna Puntos (solo tablet+) */}
      {screenWidth >= 768 && (
        <td className="text-center align-middle">
          <span >{eq.puntos || 'N/A'}</span>
        </td>
      )}
      
      {/* Columna Consecutivo para todas las ventanas*/}
        <td className="text-center align-middle">
          <span className="badge bg-secondary">{eq.consecutivo || 'N/A'}</span>
        </td>

      
      {/* Columna Estado */}
      <td className="text-center align-middle" style={{ padding: '2px 4px' }}>
        <EstadoBadge estado={eq.estado} showIcon={false} />
      </td>

      {/* Columna Patrones (tablet+, solo task=finalizar) */}
      {screenWidth >= 768 && task === 'finalizar' && (
        <td className="text-center align-middle" style={{ padding: '2px 4px' }}>
          {patronesCoverage.tipo === 'sin-config' && (
            <span className="badge bg-secondary" style={{ fontSize: 'inherit', padding: '2px 4px' }} title="Sin patrones configurados">-</span>
          )}
          {patronesCoverage.tipo === 'sin-cobertura' && (
            <span 
              style={{ 
                // Usar badge color rojo con borde para indicar sin cobertura, ya que es el caso crítico a destacar texto blanco bold
                display: 'inline-block',
                color: '#fff', // texto blanco
                fontWeight: 'bold', // texto en negrita
                backgroundColor: '#dc3545', // color rojo
                border: '2px solid #dc3545', // borde rojo
                borderRadius: '20%',
                fontSize: 'inherit', 
                padding: '4px 1px', 
                width: '40px',  
                textAlign: 'center',
                }} title="Sin movimiento activo para los patrones requeridos">
              NO
            </span>
          )}
          {patronesCoverage.tipo === 'parcial' && (
            <span className="badge bg-warning text-dark" style={{ fontSize: 'inherit', padding: '2px 4px' }} title="Cobertura parcial de patrones">
              {patronesCoverage.cubiertos}/{patronesCoverage.total}
            </span>
          )}
          {patronesCoverage.tipo === 'ok' && (
            <span 
              style={{ // Usar badge color verde para indicar cobertura total, ya que es el caso ideal a destacar texto blanco bold
                display: 'inline-block',
                color: '#fff', // texto blanco
                fontWeight: 'bold', // texto en negrita
                backgroundColor: '#28a745', // color verde
                borderRadius: '20%',
                fontSize: 'inherit', padding: '2px 4px' }} title="Todos los grupos de patrones cubiertos">
              OK
            </span>
          )}
        </td>
      )}
      
      {/* Columna Responsable (solo desktop) */}
      {screenWidth >= 992 && (
        <td className="align-middle">
          <div className="small">
            <div className="fw-semibold">{eq.responsable?.nombre || 'Sin asignar'}</div>
            {eq.fechaProcesado && (
              <div className="text-muted">
                {moment(eq.fechaProcesado).format('DD/MM/YYYY')}
              </div>
            )}
          </div>
        </td>
      )}
      
      {/* Columna Observación (solo desktop) */}
      {screenWidth >= 992 && (
        <td className="text-center align-middle">
          {eq.observacion && (
            <i 
              className="bi bi-eye-fill cursor-pointer text-primary" 
              onClick={() => handlers.onObserv(eq)}
              title="Ver observación"
            />
          )}
        </td>
      )}
      
      {/* Columna Acción */}
      <td className="text-center align-middle">
        {task === 'revisar' || screenWidth >= 992 ? (
          // Desktop: Icono según estado y task
          <div className="d-flex justify-content-center">
            {renderActionIcon(eq, task, handlers, bloquearFinalizar, titleBloqueo)}
          </div>
        ) : (
          // Móvil/Tablet: Dropdown
          <Dropdown>
            <Dropdown.Toggle 
              variant="link" 
              size="sm" 
              className="text-dark p-0"
            >
              <i className="bi bi-three-dots-vertical fs-5"/>
            </Dropdown.Toggle>
            
            <Dropdown.Menu align="end" popperConfig={{ strategy: 'fixed' }}>
              {/* Ver Detalles solo en móvil */}
              {screenWidth < 768 && (
                <>
                  <Dropdown.Item onClick={() => handlers.onVerDetalle?.(eq)}>
                    <i className="bi bi-info-circle me-2 text-info"/>
                    Ver Detalles
                  </Dropdown.Item>
                  <Dropdown.Divider />
                </>
              )}
              
              {/* Acciones según task */}
              {task === 'finalizar' && renderFinalizarOptions(eq, handlers, bloquearFinalizar, titleBloqueo)}
              {task === 'revisar' && renderRevisarOptions(eq, handlers)}
              
              {/* Acciones comunes */}
              {task === 'finalizar' && <Dropdown.Divider />}
              
              {task !== 'revisar' && (
                <>
                  <Dropdown.Item
                    onClick={() => handlers.onEdit(eq)}
                    disabled={eq.estado === 'Cancelado' || grupoInfo?.rol === 'hijo'}
                    title={grupoInfo?.rol === 'hijo'
                      ? 'Los equipos dependientes se editan a través de su equipo padre'
                      : (eq.estado === 'Cancelado' ? 'No se puede editar cancelados' : 'Editar')}
                  >
                    <i className="bi bi-pencil me-2 text-secondary"/>
                    Editar
                    {grupoInfo?.rol === 'hijo' && (
                      <span className="text-muted small ms-2">(edita desde el padre)</span>
                    )}
                  </Dropdown.Item>
                  
                  {eq.estado === 'Cancelado' ? (
                    <Dropdown.Item onClick={() => handlers.onActivate(eq)}>
                      <i className="bi bi-arrow-counterclockwise me-2 text-success"/>
                      Restaurar equipo
                    </Dropdown.Item>
                  ) : (
                    <Dropdown.Item onClick={() => handlers.onCancel(eq)}>
                      <i className="bi bi-x-circle me-2 text-danger"/>
                      Cancelar
                    </Dropdown.Item>
                  )}
                </>
              )}
              
              {/* Observación si existe */}
              {eq.observacion && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handlers.onObserv(eq)}>
                    <i className="bi bi-eye me-2 text-primary"/>
                    Ver Observación
                  </Dropdown.Item>
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </td>
      
      {/* Columnas Editar/Cancelar (solo desktop, no en revisar) */}
      {screenWidth >= 992 && task !== 'revisar' && (
        <>
          <td className="text-center align-middle">
            {(() => {
              const esDependiente = grupoInfo?.rol === 'hijo';
              const bloqueado = eq.estado === 'Cancelado' || esDependiente;
              const titleEdit = esDependiente
                ? 'Los equipos dependientes se editan desde su equipo padre'
                : (eq.estado === 'Cancelado' ? 'No se puede editar cancelados' : 'Editar');
              return (
                <i
                  className={`bi bi-pencil-square ${bloqueado ? 'text-muted' : 'cursor-pointer text-secondary'}`}
                  onClick={!bloqueado ? () => handlers.onEdit(eq) : undefined}
                  title={titleEdit}
                  style={{ cursor: bloqueado ? 'not-allowed' : 'pointer', opacity: bloqueado ? 0.4 : 1 }}
                />
              );
            })()}
          </td>
          <td className="text-center align-middle">
            {eq.estado === 'Cancelado' ? (
              <i
                className="bi bi-arrow-counterclockwise cursor-pointer text-success"
                onClick={() => handlers.onActivate(eq)}
                title="Restaurar equipo"
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <i
                className="bi bi-x-circle cursor-pointer text-danger"
                onClick={() => handlers.onCancel(eq)}
                title="Cancelar"
                style={{ cursor: 'pointer' }}
              />
            )}
          </td>
        </>
      )}
    </tr>

    {/* Modal detalle adición */}
    <ModalDetalleAdicion
      show={showAdicionModal}
      onHide={() => setShowAdicionModal(false)}
      equipo={eq}
    />
    </>
  )
}

export default EquipoCompactRow
